import { createContext, Fragment, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { getLocale, type Locale, setLocale } from "../../paraglide/runtime.js";

type LocaleState = {
  locale: Locale;
  change: (locale: Locale) => void;
};

const LocaleContext = createContext<LocaleState | null>(null);

/** Holds the language of the interface; changing it renders the app again in the new language. */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setState] = useState<Locale>(getLocale);
  const change = useCallback((next: Locale) => {
    void setLocale(next, { reload: false });
    setState(next);
  }, []);
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  const value = useMemo(() => ({ locale, change }), [locale, change]);
  return (
    <LocaleContext value={value}>
      <Fragment key={locale}>{children}</Fragment>
    </LocaleContext>
  );
}

/** The language of the interface and the way to change it. Throws outside LocaleProvider. */
export function useLocale(): LocaleState {
  const state = useContext(LocaleContext);
  if (state === null) {
    throw new Error("useLocale is used outside LocaleProvider");
  }
  return state;
}
